// Create a new component: HotelScenarioAnalysis.tsx
import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Button, MenuItem, CircularProgress, FormControl, InputLabel, Select, Grid, InputAdornment } from '@mui/material';
import { api } from '@services';
import { HotelScenarioAnalysisProps } from '@types';

const scenarioOptions = [
  { value: 'renovation', label: 'Property Renovation' },
  { value: 'occupancy_increase', label: 'Occupancy Rate Increase' },
  { value: 'rate_increase', label: 'ADR Increase' },
];

const HotelScenarioAnalysis: React.FC<HotelScenarioAnalysisProps> = ({ hotelId }) => {
  const [scenario, setScenario] = useState('');
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleRunScenario = async () => {
    setLoading(true);

    try {
      const response = await api.post(`/hotels/${hotelId}/scenario-analysis`, {
        scenario,
        parameters,
      });

      setResult(response.data);
    } catch (error) {
      console.error('Scenario analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderParameterFields = () => {
    switch (scenario) {
      case 'renovation':
        return (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Investment Amount"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={parameters.investment || ''}
                onChange={(e) => setParameters({ ...parameters, investment: Number(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected RevPAR Increase"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={parameters.revparIncrease || ''}
                onChange={(e) => setParameters({ ...parameters, revparIncrease: Number(e.target.value) })}
              />
            </Grid>
          </>
        );

      case 'occupancy_increase':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Occupancy Rate Increase"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={parameters.occupancyIncrease || ''}
              onChange={(e) => setParameters({ ...parameters, occupancyIncrease: Number(e.target.value) })}
            />
          </Grid>
        );

      case 'rate_increase':
        return (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ADR Increase"
              type="number"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              value={parameters.adrIncrease || ''}
              onChange={(e) => setParameters({ ...parameters, adrIncrease: Number(e.target.value) })}
            />
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Scenario Analysis
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Scenario</InputLabel>
              <Select
                value={scenario}
                label="Select Scenario"
                onChange={(e) => {
                  setScenario(e.target.value);
                  setParameters({});
                }}
              >
                {scenarioOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {scenario && renderParameterFields()}

          <Grid item xs={12}>
            <Button variant="contained" disabled={!scenario || loading} onClick={handleRunScenario} startIcon={loading && <CircularProgress size={20} />}>
              {loading ? 'Analyzing...' : 'Run Scenario Analysis'}
            </Button>
          </Grid>
        </Grid>

        {result && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Scenario Analysis Results
            </Typography>
            {/* Display scenario results here */}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HotelScenarioAnalysis;
